/* eslint-disable promise/prefer-await-to-then */
import type {ExtensionAPI, SnapshotDal} from '@wix/document-manager-core'
import {getReportableFromError} from '@wix/document-manager-utils'
import type {AppsInstallStateMap, Callback, Callback1, ClientSpecMap, PS} from '@wix/document-services-types'
import _ from 'lodash'
import dsConstants from '../../constants/constants'
import editorServerFacade from '../../editorServerFacade/editorServerFacade'
import platform from '../../platform/platform'
import clientSpecMapMetaData from '../../siteMetadata/clientSpecMap'
import callbackUtils from '../../utils/callbackUtils'
import {contextAdapter} from '../../utils/contextAdapter'
import {getMetaSiteId} from '../../utils/dalUtil'
import * as visitableData from '../../utils/visitableData'
import queue from '../utils/queue'
import clientSpecMapService from './clientSpecMapService'
import installedTpaAppsOnSiteService from './installedTpaAppsOnSiteService'
import pendingAppsService from './pendingAppsService'
import {
    formatAppsToAddFromAllPages,
    formatAppsToRemove,
    formatAppsToDismiss,
    formatAppsToUpdate,
    getProvisionActionsForAllPages,
    getSettleEndpoint,
    isDataEmpty,
    getProvisionActionsOfUniqueApps,
    type AppAction,
    type AppsStateDelta,
    isNewApiEnabled,
    type App,
    createUpdateAction
} from './appsStateListCreation'

const {getOnSuccessWithFallback} = callbackUtils
const {createQueue} = queue

const context = {
    save: 'save',
    load: 'load',
    firstSave: 'firstSave'
} as const

async function getSettleActions(
    extensionAPI: ExtensionAPI,
    currentPagesVisitable,
    filteredClientSpecMap,
    routerConfigMap,
    semanticAppVersions,
    appsInstallationState,
    shouldAvoidRevoking: boolean
): Promise<AppAction[] | AppsStateDelta> {
    return getSettleActionsFromAllPages(
        extensionAPI,
        currentPagesVisitable,
        filteredClientSpecMap,
        routerConfigMap,
        semanticAppVersions,
        appsInstallationState,
        shouldAvoidRevoking
    )
}

const getSettleActionsForFullSave = async (
    extensionAPI: ExtensionAPI,
    {currentPagesVisitable, clientSpecMap, routerConfigMap, semanticAppVersions, appsInstallState},
    shouldAvoidRevoking: boolean = false
) => {
    const filteredClientSpecMap = clientSpecMapService.filterApps(clientSpecMap)
    const actions = await getSettleActions(
        extensionAPI,
        currentPagesVisitable,
        filteredClientSpecMap,
        routerConfigMap,
        semanticAppVersions,
        appsInstallState,
        shouldAvoidRevoking
    )
    contextAdapter.utils.fedopsLogger.interactionStarted(
        dsConstants.PLATFORM_INTERACTIONS.SETTLE_ACTIONS,
        getInteractionsParams(actions)
    )
    return actions
}

const makeSettleRequestWithPS = (
    ps: PS,
    urlData,
    data,
    saveContext: string,
    onSuccess: Callback1<any>,
    onError: Callback1<any>
) => {
    const endpoint = getSettleEndpoint()
    const requestData = isNewApiEnabled() ? {appsStateDelta: data} : {settle: data}

    editorServerFacade.sendWithPs(
        ps,
        endpoint,
        requestData,
        getOnSuccessWithFallback('makeSettleRequestWithPS', onError, response => {
            const platformAppsExperiments = {}
            _.forEach(response.experimentsPerAppDefId, (val, key) =>
                _.set(platformAppsExperiments, key, val.appExperiments)
            )
            onSuccess({
                clientSpecMap: response.clientSpecMap,
                platformAppsExperiments,
                blocksExperiments: response.blocksExperiments?.appExperiments || {}
            })
        }),
        error => onError(error),
        urlData.queryString
    )
}

const makeSettleRequestWithPSAsync = (ps: PS, urlData, data, saveContext: string) =>
    new Promise((resolve, reject) => {
        makeSettleRequestWithPS(ps, urlData, data, saveContext, resolve, reject)
    })

const settleQueue = createQueue(makeSettleRequestWithPSAsync)
const queueMakingSettleRequestWithPSAsync = settleQueue.run

const makeSettleRequestWithImmutableSnapshot = (
    immutableSnapshot: SnapshotDal,
    data,
    onSuccess: Callback1<any>,
    onError: Callback1<any>
) => {
    editorServerFacade.sendWithSnapshotDal(
        immutableSnapshot,
        editorServerFacade.ENDPOINTS.SETTLE,
        data,
        getOnSuccessWithFallback('makeSettleRequestWithImmutableSnapshot', onError, response => {
            onSuccess({...response, payload: {clientSpecMap: response.payload}})
        }),
        (error: any) => onError(error)
    )
}

const getAppsVersions = (clientSpecMap, apps: App[]) => {
    const appDefIds = _.map(apps, 'appDefinitionId')
    return _(clientSpecMap)
        .filter(app => appDefIds.includes(app?.appDefinitionId))
        .mapKeys(app => app?.appDefinitionId)
        .mapValues(app => ({
            installedVersion: app?.appFields?.installedVersion,
            semanticVersion: app?.appFields?.semanticVersion
        }))
        .value()
}

const provision = (ps: PS, apps: App[], onSuccess: Function, onError: (e: any) => void) => {
    ps.extensionAPI.logger.interactionStarted('ProvisionThroughServer', {extras: {apps}})
    if (_.isEmpty(apps)) {
        onError('provision must be called with at least one app')
        return
    }

    const branchId = ps.extensionAPI.siteAPI.getBranchId()
    const csm = clientSpecMapMetaData.getAppsData(ps)
    const actions = getProvisionActionsOfUniqueApps(csm, apps)
    let data
    if (isNewApiEnabled()) {
        data = {
            newlyAddedApps: actions,
            newlyRemovedApps: []
        }
    } else {
        data = _.omitBy(
            {
                actions,
                siteRevision: ps.extensionAPI.siteAPI.getSiteRevision(),
                maybeBranchId: branchId
            },
            _.isNil
        )
    }

    const urlData = {
        appStoreUrl: ps.dal.get(ps.pointers.getInnerPointer(ps.pointers.general.getServiceTopology(), 'appStoreUrl')),
        metaSiteId: getMetaSiteId(ps),
        editorSessionId: ps.siteAPI.getEditorSessionId()
    }

    queueMakingSettleRequestWithPSAsync(ps, urlData, data, context.firstSave)
        .then(
            getOnSuccessWithFallback('appStoreService provision', onError, response => {
                const {platformAppsExperiments, blocksExperiments, clientSpecMap} = response
                ps.extensionAPI.logger.interactionEnded('ProvisionThroughServer', {
                    extras: {
                        versions: getAppsVersions(clientSpecMap, apps)
                    }
                })
                platform.addPlatformAppsExperiments(ps, platformAppsExperiments)
                platform.addBlocksExperiments(ps, blocksExperiments)
                onSuccess(response)
            })
        )
        .catch(onError)
}

const provisionAsync = (ps: PS, apps: App[]): Promise<any> =>
    new Promise<any>((resolve, reject) => {
        provision(ps, apps, resolve, reject)
    })

const update = async (ps: PS, apps: App[], armTags?: Record<string, string>) => {
    if (_.isEmpty(apps)) {
        throw new Error('update must be called with at least one app')
    }

    const branchId = ps.extensionAPI.siteAPI.getBranchId()
    const clientSpecMap = clientSpecMapMetaData.getAppsData(ps)
    const actions = apps.map(app => {
        const appData = _.find(clientSpecMap, {appDefinitionId: app.appDefinitionId})
        return createUpdateAction({...appData, version: app.appVersion} as any)
    })

    let data
    if (isNewApiEnabled()) {
        data = {
            newlyAddedApps: actions,
            newlyRemovedApps: []
        }
    } else {
        data = _.omitBy(
            {
                actions,
                siteRevision: ps.extensionAPI.siteAPI.getSiteRevision(),
                maybeBranchId: branchId
            },
            _.isNil
        )
    }

    const urlData = {
        appStoreUrl: ps.dal.get(ps.pointers.getInnerPointer(ps.pointers.general.getServiceTopology(), 'appStoreUrl')),
        metaSiteId: getMetaSiteId(ps),
        editorSessionId: ps.siteAPI.getEditorSessionId(),
        queryString: new URLSearchParams(armTags).toString()
    }

    return queueMakingSettleRequestWithPSAsync(ps, urlData, data, context.firstSave).then(response => {
        return isNewApiEnabled() ? response.clientSpecMap.data : response.clientSpecMap
    })
}

const getInteractionsParams = actions => ({
    extras: {actions}
})

const getSettleActionsForSave = async (
    extensionAPI: ExtensionAPI,
    {clientSpecMap, currentPagesVisitable, semanticAppVerions, routerConfigMap, appsInstallState},
    shouldAvoidRevoking?: boolean
): Promise<AppAction[] | AppsStateDelta> => {
    const filteredClientSpecMap = clientSpecMapService.filterApps(clientSpecMap)

    const actions = await getSettleActions(
        extensionAPI,
        currentPagesVisitable,
        filteredClientSpecMap,
        routerConfigMap,
        semanticAppVerions,
        appsInstallState,
        shouldAvoidRevoking
    )

    contextAdapter.utils.fedopsLogger.interactionStarted(
        dsConstants.PLATFORM_INTERACTIONS.SETTLE_ACTIONS,
        getInteractionsParams(actions)
    )
    // @ts-ignore
    return actions
}

const settleOnSaveWithImmutableSnapshot = async function (
    extensionAPI: ExtensionAPI | PS['extensionAPI'],
    currentImmutableSnapshot: SnapshotDal,
    appServiceData,
    revision: string | number,
    urlData,
    shouldAvoidRevoking: boolean,
    onSuccess: Callback1<any>,
    onError: Callback1<any>
) {
    const {branchId} = appServiceData
    let data
    if (isNewApiEnabled()) {
        data = await getSettleActionsForSave(extensionAPI, appServiceData, shouldAvoidRevoking)
    } else {
        data = _.omitBy(
            {
                actions: await getSettleActionsForSave(extensionAPI, appServiceData, shouldAvoidRevoking),
                siteRevision: revision,
                maybeBranchId: branchId
            },
            _.isNil
        )
    }

    if (!isDataEmpty(data)) {
        extensionAPI.logger.interactionStarted('makeSettleRequestWithImmutableSnapshot')
        makeSettleRequestWithImmutableSnapshot(currentImmutableSnapshot, data, onSuccess, onError)
        extensionAPI.logger.interactionEnded('makeSettleRequestWithImmutableSnapshot')
    } else {
        onSuccess(undefined)
    }
}

const settleOnLoad = async function (ps: PS, shouldAvoidRevoking?: boolean, callback?: Callback) {
    const onSuccess = getOnSuccessWithFallback('settleOnLoad', callback, function (response) {
        ps.extensionAPI.logger.interactionEnded('settleOnLoad', {
            extras: {response}
        })
        if (response) {
            platform.addPlatformAppsExperiments(ps, response.platformAppsExperiments)
            platform.addBlocksExperiments(ps, response.blocksExperiments)
            const csm = isNewApiEnabled() ? response.clientSpecMap.data : response.clientSpecMap
            _.forEach(csm, appData => clientSpecMapService.registerAppData(ps, appData))
        }
        if (response?.success) {
            const csm = isNewApiEnabled() ? response.payload.clientSpecMap.data : response.payload.clientSpecMap
            _.forEach(csm, appData => clientSpecMapService.registerAppData(ps, appData))
        }
        if (callback) {
            callback()
        }
    })

    const visitableDataAllPages = visitableData.createFromPrivateServices(ps)
    const clientSpecMap = clientSpecMapService.filterApps(ps.dal.get(ps.pointers.general.getClientSpecMap()))
    const routerConfigMap = ps.dal.get(ps.pointers.routers.getRoutersConfigMapPointer())
    const siteRevision = ps.extensionAPI.siteAPI.getSiteRevision()
    const urlData = {
        appStoreUrl: ps.dal.get(ps.pointers.getInnerPointer(ps.pointers.general.getServiceTopology(), 'appStoreUrl')),
        metaSiteId: getMetaSiteId(ps),
        editorSessionId: ps.siteAPI.getEditorSessionId()
    }
    await settleImplOnLoad(
        ps,
        visitableDataAllPages,
        clientSpecMap,
        routerConfigMap,
        siteRevision,
        urlData,
        shouldAvoidRevoking,
        onSuccess
    )
}

export const processAppsAndGenerateActions = async (
    extensionAPI: ExtensionAPI | PS['extensionAPI'],
    clientSpecMap: ClientSpecMap,
    visitableDataAllPages: any,
    routerConfigMap: any,
    options: {
        shouldAvoidRevoking?: boolean
        excludeHybrid?: boolean
        removeProvisionedFromGrant?: boolean
        includePendingApps?: boolean
        shouldGetProvisionForAllPages?: boolean
    } = {},
    semanticAppVersions?: Record<string, string>,
    providedAppsState?: AppsInstallStateMap
): Promise<{
    newlyRemovedApps?: (AppAction | string)[]
    newlyAddedApps?: (AppAction | string)[]
    appsToProvision?: AppAction[]
    allActions?: (AppAction | string)[]
}> => {
    const {
        shouldAvoidRevoking = false,
        excludeHybrid = false,
        removeProvisionedFromGrant = false,
        includePendingApps = false,
        shouldGetProvisionForAllPages = false
    } = options
    const appsState = providedAppsState || getAppState(extensionAPI)

    const {grant, revoke} = await installedTpaAppsOnSiteService.getAppsToGrantAndRevoke(
        extensionAPI,
        clientSpecMap,
        visitableDataAllPages,
        {shouldAvoidRevoking, excludeHybrid},
        appsState
    )

    let appsToGrant = [...grant]
    let appsToProvision: AppAction[] = []

    if (removeProvisionedFromGrant) {
        appsToGrant = _.concat(appsToGrant, _.filter(clientSpecMap, 'notProvisioned' as any))
    }

    if (includePendingApps) {
        appsToGrant = _.concat(appsToGrant, pendingAppsService.getAppsToAdd())
    }

    if (shouldGetProvisionForAllPages) {
        appsToProvision = _.concat(getProvisionActionsForAllPages(clientSpecMap, semanticAppVersions))
    }

    if (isNewApiEnabled()) {
        const {newlyAddedApps, newlyRemovedApps} = categorizeAppsIntoList(
            appsToGrant,
            revoke,
            clientSpecMap,
            semanticAppVersions,
            appsToProvision,
            appsState
        )
        return {
            newlyRemovedApps,
            newlyAddedApps,
            appsToProvision
        }
    }
    const {allActions} = categorizeAppsIntoActions(
        appsToGrant,
        revoke,
        clientSpecMap,
        semanticAppVersions,
        appsToProvision,
        appsState
    )

    return {
        appsToProvision,
        allActions
    }
}

const categorizeAppsIntoList = (
    appsToGrant: any[],
    appsToRevoke: any[],
    clientSpecMap: ClientSpecMap,
    semanticAppVersions?: Record<string, string>,
    appsToProvision?: AppAction[],
    appsState: any = {}
): {
    newlyRemovedApps: (AppAction | string)[]
    newlyAddedApps: AppAction[]
} => {
    const appsToAdd = formatAppsToAddFromAllPages(
        appsToGrant,
        appsToRevoke,
        clientSpecMap,
        semanticAppVersions,
        appsToProvision
    )
    const appsToRemove = formatAppsToRemove(appsToRevoke)
    const appsToDismiss = formatAppsToDismiss()
    const appsToUpdate = formatAppsToUpdate(clientSpecMap, appsToGrant, appsToRevoke, appsState)

    const newlyAddedApps = [...appsToAdd, ...appsToUpdate]
    const newlyRemovedApps = [...appsToRemove, ...appsToDismiss]

    return {
        newlyRemovedApps,
        newlyAddedApps
    }
}

const categorizeAppsIntoActions = (
    appsToGrant: any[],
    appsToRevoke: any[],
    clientSpecMap: ClientSpecMap,
    semanticAppVersions?: any,
    appsToProvision?: any[],
    appsState: any = {}
) => {
    const appsToAdd = formatAppsToAddFromAllPages(
        appsToGrant,
        appsToRevoke,
        clientSpecMap,
        semanticAppVersions,
        appsToProvision
    )
    const appsToRemove = formatAppsToRemove(appsToRevoke)
    const appsToDismiss = formatAppsToDismiss()
    const appsToUpdate = formatAppsToUpdate(clientSpecMap, appsToGrant, appsToRevoke, appsState)

    return {
        allActions: _.concat(appsToAdd, appsToRemove, appsToDismiss, appsToUpdate)
    }
}

const getAppState = (extensionAPI: ExtensionAPI | PS['extensionAPI']) =>
    extensionAPI.appsInstallState.getAllAppsInstallStatus()

const settleImplOnLoad = async (
    ps: PS,
    visitableDataAllPages: any,
    clientSpecMap: ClientSpecMap,
    routerConfigMap,
    siteRevision: string,
    urlData,
    shouldAvoidRevoking,
    callback
) => {
    let data
    const branchId = ps.extensionAPI.siteAPI.getBranchId()

    if (isNewApiEnabled()) {
        const {newlyRemovedApps, newlyAddedApps} = await processAppsAndGenerateActions(
            ps.extensionAPI,
            clientSpecMap,
            visitableDataAllPages,
            routerConfigMap,
            {
                shouldAvoidRevoking,
                excludeHybrid: true
            }
        )
        data = {
            newlyRemovedApps,
            newlyAddedApps
        }
    } else {
        const {allActions} = await processAppsAndGenerateActions(
            ps.extensionAPI,
            clientSpecMap,
            visitableDataAllPages,
            routerConfigMap,
            {
                shouldAvoidRevoking,
                excludeHybrid: true
            }
        )

        data = _.omitBy(
            {
                actions: allActions,
                siteRevision,
                maybeBranchId: branchId
            },
            _.isNil
        )
    }

    const isEmpty = isDataEmpty(data)

    if (!isEmpty) {
        ps.extensionAPI.logger.interactionStarted('settleOnLoad', {extras: data.actions})
        //chnage the reportStateDifferenceByActions
        ps.extensionAPI.appsInstallState.reportStateDifferenceByActions(clientSpecMap, data.actions, 'settleOnLoad')
        queueMakingSettleRequestWithPSAsync(ps, urlData, data, context.load)
            .then(callback)
            .catch(e => {
                ps.extensionAPI.logger.captureError(
                    getReportableFromError(e, {
                        errorType: 'settleImplOnLoadError',
                        message: `Failed settleImplOnLoad`,
                        tags: {
                            provisioningOnSuccess: true
                        }
                    })
                )

                console.error(e)
                callback()
            })
    } else {
        callback()
    }
}

const getSettleActionsFromAllPages = async (
    extensionAPI,
    currentPagesVisitable,
    clientSpecMap: ClientSpecMap,
    routerConfigMap,
    semanticAppVersions,
    appsInstallationState: AppsInstallStateMap,
    shouldAvoidRevoking: boolean
): Promise<AppAction[] | AppsStateDelta> => {
    if (isNewApiEnabled()) {
        const {newlyAddedApps, newlyRemovedApps, appsToProvision} = await processAppsAndGenerateActions(
            extensionAPI,
            clientSpecMap,
            currentPagesVisitable,
            routerConfigMap,
            {
                shouldAvoidRevoking,
                shouldGetProvisionForAllPages: true,
                removeProvisionedFromGrant: true,
                includePendingApps: true
            },
            semanticAppVersions,
            appsInstallationState
        )

        return {
            newlyAddedApps: _.concat(appsToProvision, newlyAddedApps),
            newlyRemovedApps
        } as AppsStateDelta
    }
    const {allActions, appsToProvision} = await processAppsAndGenerateActions(
        extensionAPI,
        clientSpecMap,
        currentPagesVisitable,
        routerConfigMap,
        {
            shouldAvoidRevoking,
            shouldGetProvisionForAllPages: true,
            removeProvisionedFromGrant: true,
            includePendingApps: true
        },
        semanticAppVersions,
        appsInstallationState
    )

    return _.concat(appsToProvision, allActions) as AppAction[]
}

const settleOnLoadAsync = (ps: PS, shouldAvoidRevoking?: boolean) =>
    new Promise<void>(res => settleOnLoad(ps, shouldAvoidRevoking, res))

export default {
    provision,
    provisionAsync,
    update,
    settleOnSaveWithImmutableSnapshot,
    getSettleActionsForFullSave,
    getSettleActionsForSave,
    settleOnLoad,
    settleOnLoadAsync,
    waitForSettleQueue: settleQueue.wait
}
